import classNames from "classnames";
import React, { Component, createRef, Fragment } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import Form, { Fields, FormData } from "Shared/Form";
import FormCheckbox from "Shared/Form/Checkbox";
import { CanadianPostalCode, required, USZipCode } from "Shared/Form/validators";
import styled from "styled-components";
import ArrowRightIcon from "src/components/icons/ArrowRight";
import { getRegion } from "src/utils";
import { isUS } from "src/utils/account";
import { subtractYears } from "src/utils/date";
import { Account } from "Types/api/Account";
import StepContent from "../../StepContent/StepContent";
import { BaseStepProps } from "../shared/BaseStepProps";
import { mapFieldsToValues } from "../shared/fieldValueMapper";
import { extraFieldsPerRow, fieldsPerRow, getExtraFields, getFields } from "./models";

const TRANSLATION_KEY = "ContinueDeal.CreditApp.Residence";

const Title = styled("h3")`
    border-bottom: 2px solid #e5e5e5;
    color: #333;
    font-size: 1.5625em;
    font-weight: 400;
    margin: 15px 0 0;
    padding: 0 0 15px;

    ${(props) => props.theme.breakpoints.up("desktop")} {
        font-size: 1.875em;
        padding: 0 0 8px;
    }
`;

const FormCheckboxWrapper = styled("div")`
    align-items: flex-end;
    display: flex;
    flex-flow: column;
`;

const StyledForm = styled(Form)<{ isCosigner?: boolean }>`
    margin-bottom: 20px;
    margin-top: ${(props) => (props.isCosigner ? 0 : 20)}px;

    ${(props) => props.theme.breakpoints.up("desktop")} {
        margin-bottom: 30px;
        margin-top: ${(props) => (props.isCosigner ? 0 : 30)}px;
    }
`;
const ArrowRight = styled(ArrowRightIcon)`
    color: ${(props) => props.theme.palette.common.white};
    height: 10px;
    width: 10px;
`;
const Dot = styled("span")`
    background-color: ${(props) => props.theme.palette.primary.main};
    border-radius: 10px;
    display: inline-block;
    height: 10px;
    padding: 5px;
    width: 10px;

    svg {
        vertical-align: top;
    }
`;
const Message = styled("span")`
    color: ${(props) => props.theme.palette.grey[80]};
    display: inline-block;
    font-size: 14px;
    font-weight: 500;
    padding-left: 8px;
`;
const MessageWrapper = styled("div")`
    align-items: center;
    display: flex;

    ${(props) => props.theme.breakpoints.up("desktop")} {
        padding: 10px;
    }
`;

// tslint:disable-next-line:no-empty-interface
export interface ResidenceProps extends BaseStepProps {
    account: Account;
    zipCode: string;
    zipCodeSource: string;
    useSameAddress: (value: { target: { value: boolean } }) => void;
    onFormChanged?: (data: any) => void;
}

export interface ResidenceState {
    sameAddressChecked: boolean;
}

class Residence extends Component<ResidenceProps & WithTranslation, ResidenceState> {
    private region: string;
    private initialFields: Fields;
    private initialExtraFields: Fields;

    private residenceForm = createRef<Form>();
    private extraResidenceForm = createRef<Form>();

    constructor(props: ResidenceProps & WithTranslation) {
        super(props);

        this.state = {
            sameAddressChecked: false,
        };

        const { account } = this.props;

        this.region = getRegion(account);

        this.initialFields = getFields({ region: this.region });
        this.initialExtraFields = getExtraFields({ region: this.region });
    }

    public render() {
        const { className, isCosigner = false, formData = {}, t } = this.props;

        const fields = mapFieldsToValues(this.initialFields, formData);
        this.buildZipField(fields, this.region);

        const extraFields = mapFieldsToValues(this.initialExtraFields, formData);
        this.buildExtraZipField(extraFields, this.region);

        return (
            <div className={classNames("Residence", className)}>
                <StepContent onBackClick={this.handleBackClick} onNextClick={this.handleNextClick}>
                    <Title>
                        {isCosigner ? t(TRANSLATION_KEY + ".cosignerTitle") : t(TRANSLATION_KEY + ".defaultTitle")}
                    </Title>
                    {isCosigner && (
                        <FormCheckboxWrapper>
                            <FormCheckbox
                                name={"sameAddress"}
                                label={t(TRANSLATION_KEY + ".sameAddressLabel")}
                                value={this.state.sameAddressChecked}
                                onChange={this.handleOnSameAddressChange}
                            />
                        </FormCheckboxWrapper>
                    )}

                    {/* the 'key' is a hack to make the form re-render because its not controlled */}
                    <StyledForm
                        key={this.state.sameAddressChecked.toString()}
                        fields={fields}
                        fieldsPerRow={fieldsPerRow}
                        ref={this.residenceForm}
                        onChange={this.handleFormChange}
                        isCosigner={isCosigner}
                    />

                    {this.isNewlyMoved(formData.movingDate) && (
                        <Fragment>
                            <MessageWrapper>
                                <Dot>
                                    <ArrowRight />
                                </Dot>
                                <Message>{t(TRANSLATION_KEY + ".previousAddressMessage")}</Message>
                            </MessageWrapper>
                            <StyledForm
                                fields={extraFields}
                                fieldsPerRow={extraFieldsPerRow}
                                ref={this.extraResidenceForm}
                            />
                        </Fragment>
                    )}
                </StepContent>
            </div>
        );
    }

    private handleOnSameAddressChange = (value: { target: { value: boolean } }) => {
        this.setState({
            sameAddressChecked: value.target.value,
        });

        this.props.useSameAddress(value);
    };

    private handleFormChange = (data: FormData) => {
        this.props.onFormChanged && this.props.onFormChanged(data);
    };

    private handleNextClick = () => {
        if (
            this.residenceForm.current!.isFormValid() &&
            (!this.extraResidenceForm.current || this.extraResidenceForm.current.isFormValid())
        ) {
            const extra = this.extraResidenceForm.current ? this.extraResidenceForm.current.getFormData() : {};
            const formData = {
                ...this.residenceForm.current!.getFormData(),
                ...extra,
            };
            return this.props.onNextClick(formData);
        }
    };

    private handleBackClick = () => {
        const extra = this.extraResidenceForm.current ? this.extraResidenceForm.current.getFormData() : {};
        const formData = {
            ...this.residenceForm.current!.getFormData(),
            ...extra,
        };
        return this.props.onBackClick(formData);
    };

    private isNewlyMoved = (movingDate: Date | undefined): boolean => {
        if (!movingDate) {
            return false;
        }

        const today = new Date();
        const minMovingDate = subtractYears(today, 2);

        return minMovingDate.getTime() < movingDate.getTime();
    };

    private buildZipField = (fields: Fields, region: string) => {
        fields.zipCode.inputProps.region = region;
        fields.zipCode.validators = [required, isUS(region) ? USZipCode : CanadianPostalCode];
    };

    private buildExtraZipField = (fields: Fields, region: string) => {
        fields.extraZipCode.inputProps.region = region;
        fields.extraZipCode.validators = [required, isUS(region) ? USZipCode : CanadianPostalCode];
    };
}

export default withTranslation()(Residence);
